/** Observed weather vector tiles (ERA5 / CHIRPS monthly rollups at service-area cells). */

import type { FillLayerSpecification, ExpressionSpecification } from 'mapbox-gl';
import {
  buildInterpolateFillColor,
  type ChoroplethColorStop,
  type ChoroplethLegendSpec,
} from './choroplethLegend';

export const OBSERVED_WEATHER_TILE_MIN_ZOOM = 6;
export const OBSERVED_WEATHER_TILE_MAX_ZOOM = 13;
export const OBSERVED_WEATHER_LAYER_MAX_ZOOM = 22;
/** Direction arrows share the same zoom range as wind speed fill. */
export const WIND_ARROW_MIN_ZOOM = OBSERVED_WEATHER_TILE_MIN_ZOOM;
export const WIND_ARROW_MAX_ZOOM = OBSERVED_WEATHER_LAYER_MAX_ZOOM;

export type ObservedWeatherLayerKey =
  | 'observedWeatherTemp'
  | 'observedWeatherWind'
  | 'observedWeatherWindDirection'
  | 'observedWeatherPrecip';

export type ObservedWeatherPeriodKey =
  | 'observedWeatherTempPeriods'
  | 'observedWeatherWindPeriods'
  | 'observedWeatherWindDirectionPeriods'
  | 'observedWeatherPrecipPeriods';

export interface ObservedWeatherLayerDef {
  settingsKey: ObservedWeatherLayerKey;
  periodSettingsKey: ObservedWeatherPeriodKey;
  sectionTitle: string;
  toggleLabel: string;
  /** Short blurb — what it is and why it helps on the map. */
  summary: string;
  tileLayer: string;
  sourceLayer: string;
  sourceIdPrefix: string;
  fillLayerIdPrefix: string;
  paint: NonNullable<FillLayerSpecification['paint']>;
  legend: ChoroplethLegendSpec;
  info: string;
}

const BASE_OPACITY = 0.72;

const TEMP_COLOR_STOPS: ChoroplethColorStop[] = [
  { value: 18, color: '#2563eb' },
  { value: 24, color: '#22c55e' },
  { value: 28, color: '#eab308' },
  { value: 32, color: '#f97316' },
  { value: 36, color: '#dc2626' },
];

const WIND_SPEED_COLOR_STOPS: ChoroplethColorStop[] = [
  { value: 0, color: '#e0f2fe' },
  { value: 4, color: '#38bdf8' },
  { value: 8, color: '#6366f1' },
  { value: 12, color: '#a855f7' },
  { value: 16, color: '#7e22ce' },
];

const WIND_DIRECTION_COLOR_STOPS: ChoroplethColorStop[] = [
  { value: 0, color: '#ef4444' },
  { value: 90, color: '#22c55e' },
  { value: 180, color: '#3b82f6' },
  { value: 270, color: '#eab308' },
  { value: 360, color: '#ef4444' },
];

const PRECIP_COLOR_STOPS: ChoroplethColorStop[] = [
  { value: 0, color: '#f8fafc' },
  { value: 10, color: '#bae6fd' },
  { value: 30, color: '#0284c7' },
  { value: 60, color: '#1d4ed8' },
  { value: 120, color: '#312e81' },
];

function observedWeatherFillPaint(stops: readonly ChoroplethColorStop[]): NonNullable<FillLayerSpecification['paint']> {
  return {
    'fill-color': buildInterpolateFillColor(stops) as ExpressionSpecification,
    'fill-opacity': BASE_OPACITY,
  };
}

export const OBSERVED_WEATHER_LAYER_DEFS: ObservedWeatherLayerDef[] = [
  {
    settingsKey: 'observedWeatherTemp',
    periodSettingsKey: 'observedWeatherTempPeriods',
    sectionTitle: 'Seasonal Temperature',
    toggleLabel: 'Temperature grid',
    summary: 'See how warm or cool each area was in a given month. Useful for spotting hot seasons before you travel or work outdoors.',
    tileLayer: 'weather_observed_temp',
    sourceLayer: 'weather_observed_temp',
    sourceIdPrefix: 'ghana-observed-weather-temp-',
    fillLayerIdPrefix: 'ghana-observed-weather-temp-fill-',
    info: 'Average air temperature for each map square, in °C.',
    paint: observedWeatherFillPaint(TEMP_COLOR_STOPS),
    legend: { title: 'Temperature', unit: '°C', stops: TEMP_COLOR_STOPS },
  },
  {
    settingsKey: 'observedWeatherWind',
    periodSettingsKey: 'observedWeatherWindPeriods',
    sectionTitle: 'Seasonal Wind',
    toggleLabel: 'Wind speed & direction',
    summary: 'See where winds were strongest and which way they blew each week. Speed and direction arrows appear together from z6.',
    tileLayer: 'weather_observed_wind',
    sourceLayer: 'weather_observed_wind',
    sourceIdPrefix: 'ghana-observed-weather-wind-',
    fillLayerIdPrefix: 'ghana-observed-weather-wind-fill-',
    info: 'Speed (m/s) as colour; arrows show mean wind direction at each cell centre (meteorological degrees, 0 = from north).',
    paint: observedWeatherFillPaint(WIND_SPEED_COLOR_STOPS),
    legend: { title: 'Wind speed', unit: 'm/s', stops: WIND_SPEED_COLOR_STOPS },
  },
  {
    settingsKey: 'observedWeatherWindDirection',
    periodSettingsKey: 'observedWeatherWindDirectionPeriods',
    sectionTitle: 'Seasonal Wind Direction',
    toggleLabel: 'Wind direction grid',
    summary: 'Internal — paired with wind speed layer (arrow symbols).',
    tileLayer: 'weather_observed_wind_direction',
    sourceLayer: 'weather_observed_wind_direction',
    sourceIdPrefix: 'ghana-observed-weather-wind-direction-',
    fillLayerIdPrefix: 'ghana-observed-weather-wind-direction-fill-',
    info: 'Wind direction at cell centroids.',
    paint: observedWeatherFillPaint(WIND_DIRECTION_COLOR_STOPS),
    legend: { title: 'Wind direction', unit: '°', stops: WIND_DIRECTION_COLOR_STOPS },
  },
  {
    settingsKey: 'observedWeatherPrecip',
    periodSettingsKey: 'observedWeatherPrecipPeriods',
    sectionTitle: 'Seasonal Precipitation',
    toggleLabel: 'Precipitation grid',
    summary: 'See how much rain fell in each area during a given month. Compare wet and dry seasons alongside flood risk.',
    tileLayer: 'weather_observed_precip',
    sourceLayer: 'weather_observed_precip',
    sourceIdPrefix: 'ghana-observed-weather-precip-',
    fillLayerIdPrefix: 'ghana-observed-weather-precip-fill-',
    info: 'Total rainfall for each map square, in millimetres.',
    paint: observedWeatherFillPaint(PRECIP_COLOR_STOPS),
    legend: { title: 'Precipitation', unit: 'mm', stops: PRECIP_COLOR_STOPS },
  },
];

/** Layer toggle keys managed by useObservedWeatherLayers (not LAYER_IDS). */
export const OBSERVED_WEATHER_LAYER_KEYS = OBSERVED_WEATHER_LAYER_DEFS.map((d) => d.settingsKey);

/** Settings panel — wind speed + direction are one combined control. */
export const OBSERVED_WEATHER_UI_DEFS = OBSERVED_WEATHER_LAYER_DEFS.filter(
  (d) => d.settingsKey !== 'observedWeatherWindDirection',
);

export const WIND_SPEED_DEF = OBSERVED_WEATHER_LAYER_DEFS.find((d) => d.settingsKey === 'observedWeatherWind')!;
export const WIND_DIRECTION_DEF = OBSERVED_WEATHER_LAYER_DEFS.find(
  (d) => d.settingsKey === 'observedWeatherWindDirection',
)!;

export function latestTiledPeriod(periods: string[]): string | undefined {
  if (periods.length === 0) return undefined;
  const sorted = [...periods].sort();
  return sorted[sorted.length - 1];
}

export function observedWeatherSourceId(def: ObservedWeatherLayerDef, period: string): string {
  return `${def.sourceIdPrefix}${period}`;
}

export function observedWeatherFillLayerId(def: ObservedWeatherLayerDef, period: string): string {
  return `${def.fillLayerIdPrefix}${period}`;
}

export function observedWeatherWindArrowLayerId(period: string): string {
  return `ghana-observed-weather-wind-arrow-${period}`;
}

export function formatObservedWeatherMonthPill(periodStart: string): string {
  const d = new Date(`${periodStart}T00:00:00`);
  if (Number.isNaN(d.getTime())) return periodStart;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function observedWeatherPeriodTilesUrl(tileRoot: string, tileLayer: string, period: string): string {
  const base = tileRoot.replace(/\/$/, '');
  return `${base}/tiles/${tileLayer}/periods/${encodeURIComponent(period)}/{z}/{x}/{y}.pbf`;
}
